<script lang="ts" setup>
import { computed, toRef, watch } from "vue";
import { useProductPreviewCandidates } from "@/queries/products";

interface Props {
  productId: string;
  /** When true, candidates flagged as currently-selected seed the model on first load. */
  preselectCurrent?: boolean;
  /** Disable interaction (read-only display). */
  disabled?: boolean;
}

const props = defineProps<Props>();
const selected = defineModel<string[]>({ required: true });

const productIdRef = toRef(props, "productId");
const enabled = computed(() => !!props.productId);
const { previewCandidates, previewCandidatesLoading } = useProductPreviewCandidates(productIdRef, enabled);

// Seed from server-flagged "currently selected" once the candidates land — but only when the parent
// asks for it (publish modal opens a fresh picker, edit modal preselects the active membership).
let seeded = false;
watch(previewCandidates, (candidates) => {
  if (!props.preselectCurrent || seeded || !candidates.length) return;
  const preselected = candidates.filter((c) => c.isCurrentlySelected).map((c) => c.orgId);
  selected.value = preselected;
  seeded = true;
});

function toggleOrg(orgId: string) {
  if (props.disabled) return;
  const next = new Set(selected.value);
  if (next.has(orgId)) next.delete(orgId);
  else next.add(orgId);
  selected.value = Array.from(next);
}

function isSelected(orgId: string) {
  return selected.value.includes(orgId);
}
</script>

<template>
  <div>
    <div v-if="previewCandidatesLoading" class="text-muted small">
      <span class="spinner-border spinner-border-sm me-2"></span>Loading candidates…
    </div>
    <div v-else-if="!previewCandidates.length" class="text-muted small">
      No candidates available. Add at least one subscriber to this product first.
    </div>
    <div v-else class="border rounded p-2" style="max-height: 240px; overflow-y: auto;">
      <div
        v-for="candidate in previewCandidates"
        :key="candidate.orgId"
        class="form-check"
      >
        <input
          class="form-check-input"
          type="checkbox"
          :id="`picker-${candidate.orgId}`"
          :checked="isSelected(candidate.orgId)"
          :disabled="disabled"
          @change="toggleOrg(candidate.orgId)"
        />
        <label class="form-check-label" :for="`picker-${candidate.orgId}`">
          <strong>{{ candidate.orgName }}</strong>
          <span v-if="candidate.isPublisherOrg" class="badge bg-secondary ms-2">You</span>
        </label>
      </div>
    </div>
  </div>
</template>
